import { getActiveLocale } from '../../core/utils/activeLocale'
import { MICROCENTS_PER_CENT } from '../prices'

/** Cents in the currency's unit: every amount and rate here is counted in cents. */
const CENTS_PER_UNIT = 100

/** The most decimals a rate is written with: eight decimals of a euro are the micro-cent. */
const RATE_MAX_FRACTION_DIGITS = 8

/**
 * Format a price in cents to a localized currency string.
 *
 * The locale follows the language the app is displaying, and is not a constant:
 * it used to default to `fr-FR`, so an English page read « Publishing a channel
 * is a **10,00 €** per month subscription » — the sentence translated, the
 * number did not. Only the formatting follows the language; the currency does
 * not, so an English reader sees `€10.00` and not a converted amount.
 *
 * @param cents - The amount in cents (e.g., 1000 = 10.00)
 * @param currency - ISO 4217 currency code (e.g., 'EUR', 'USD')
 * @param locale - Locale for formatting; defaults to the displayed language
 * @returns Formatted price string (e.g., '10,00 €' in French, '€10.00' in English)
 */
export function formatPrice(cents: number, currency = 'EUR', locale?: string): string {
  return new Intl.NumberFormat(locale ?? getActiveLocale(), {
    style: 'currency',
    currency,
  }).format(cents / CENTS_PER_UNIT)
}

/**
 * Format a **rate** in cents — a price per unit: a minute, a gigabyte, a month — with the decimals
 * it needs: the cents at least, down to the micro-cent a price is quoted in, trailing zeros past the
 * cents dropped. 3,5 cents is « 0,035 € », 0,3 cent « 0,003 € », 10 cents « 0,10 € ». An amount —
 * a total, a balance, what a charge debits — is {@link formatPrice}'s, in two decimals.
 *
 * The figure is rounded to the micro-cent first, so a rate computed on doubles prints none of its
 * float noise.
 *
 * @param cents - The rate in cents, fractional where negotiated so (3.5 = 0,035 €)
 * @param currency - ISO 4217 currency code
 * @param locale - Locale for formatting; defaults to the displayed language
 */
export function formatRate(cents: number, currency = 'EUR', locale?: string): string {
  const microcents = Math.round(cents * MICROCENTS_PER_CENT)
  return new Intl.NumberFormat(locale ?? getActiveLocale(), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: RATE_MAX_FRACTION_DIGITS,
  }).format(microcents / (MICROCENTS_PER_CENT * CENTS_PER_UNIT))
}
